import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCart } from "@/hooks/useCart";
import type { ProductDetail } from "@/mocks/products";

interface QuickViewModalProps {
  product: ProductDetail;
  onClose: () => void;
}

export default function QuickViewModal({ product, onClose }: QuickViewModalProps) {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith("ar");
  const { addItem } = useCart();

  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedVariants, setSelectedVariants] = useState<Record<string, string>>({});
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    if (product.variants) {
      const initial: Record<string, string> = {};
      product.variants.forEach((v) => {
        initial[v.name] = v.options[0];
      });
      setSelectedVariants(initial);
    }
    setSelectedImage(0);
    setQuantity(1);
  }, [product]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const name = isAr ? product.nameAr : product.nameEn;
  const discount = product.compareAtPrice
    ? Math.round(((product.compareAtPrice - product.price) / product.compareAtPrice) * 100)
    : 0;

  const handleAddToCart = () => {
    if (!product.inStock) return;
    addItem({
      productId: product.id,
      nameEn: product.nameEn,
      nameAr: product.nameAr,
      slug: product.slug,
      price: product.price,
      imageUrl: product.images[0],
      quantity,
      maxStock: product.stockCount,
      selectedVariants,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      <div
        className={`relative bg-background-50 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto ${isAr ? "rtl" : "ltr"}`}
        dir={isAr ? "rtl" : "ltr"}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-background-100 hover:bg-background-200 flex items-center justify-center text-foreground-500 hover:text-foreground-900 transition-colors cursor-pointer"
          aria-label={t("products.close")}
        >
          <i className="ri-close-line text-lg"></i>
        </button>

        <div className={`grid grid-cols-1 md:grid-cols-2 ${isAr ? "md:flex-row-reverse" : ""}`}>
          {/* Image Gallery */}
          <div className="p-6 md:p-8">
            <div className="flex gap-3">
              {/* Thumbnails */}
              <div className="flex flex-col gap-2 flex-shrink-0">
                {product.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`w-12 h-12 md:w-14 md:h-14 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                      selectedImage === idx
                        ? "border-primary-500"
                        : "border-background-200 hover:border-secondary-300"
                    }`}
                  >
                    <img
                      src={img}
                      alt={`${name} ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>

              {/* Main image */}
              <div className="flex-1 aspect-square rounded-xl overflow-hidden bg-background-100">
                <img
                  src={product.images[selectedImage]}
                  alt={name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>

          {/* Product Info */}
          <div className="p-6 md:p-8 border-t md:border-t-0 md:border-l border-background-200">
            {/* Badges */}
            <div className="flex items-center gap-2 mb-3">
              {product.isSale && product.compareAtPrice && (
                <span className="px-2.5 py-1 bg-primary-500 text-white text-xs font-semibold rounded-md whitespace-nowrap">
                  -{discount}% OFF
                </span>
              )}
              {product.isNew && (
                <span className="px-2.5 py-1 bg-foreground-950 text-white text-xs font-semibold rounded-md whitespace-nowrap">
                  NEW
                </span>
              )}
            </div>

            {/* Name */}
            <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-2 line-clamp-2">
              {name}
            </h3>

            {/* Rating */}
            <div className="flex items-center gap-2 mb-3">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <i
                    key={i}
                    className={`text-xs ${
                      i < Math.floor(product.rating)
                        ? "ri-star-fill text-accent-500"
                        : i < product.rating
                        ? "ri-star-half-line text-accent-500"
                        : "ri-star-line text-foreground-300"
                    }`}
                  ></i>
                ))}
              </div>
              <span className="text-xs text-foreground-500">({product.reviewCount})</span>
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-3 mb-4">
              <span className="text-2xl font-bold text-foreground-950">
                ${product.price.toFixed(2)}
              </span>
              {product.compareAtPrice && (
                <>
                  <span className="text-base text-foreground-400 line-through">
                    ${product.compareAtPrice.toFixed(2)}
                  </span>
                  <span className="text-xs font-medium text-primary-600">
                    Save ${(product.compareAtPrice - product.price).toFixed(2)}
                  </span>
                </>
              )}
            </div>

            {/* Stock */}
            <div className="flex items-center gap-2 mb-4">
              {product.inStock ? (
                <>
                  <i className="ri-checkbox-circle-fill text-green-600 text-sm"></i>
                  <span className="text-xs text-green-700 font-medium">
                    {t("product.in_stock")}
                  </span>
                  {product.stockCount < 20 && (
                    <span className="text-[11px] text-accent-700 bg-accent-50 px-2 py-0.5 rounded-full">
                      Only {product.stockCount} left
                    </span>
                  )}
                </>
              ) : (
                <>
                  <i className="ri-close-circle-fill text-red-500 text-sm"></i>
                  <span className="text-xs text-red-600 font-medium">
                    {t("product.out_of_stock")}
                  </span>
                </>
              )}
            </div>

            {/* Variants */}
            {product.variants && product.variants.length > 0 && (
              <div className="space-y-3 mb-5">
                {product.variants.map((variant) => (
                  <div key={variant.name}>
                    <h4 className="text-xs font-semibold text-foreground-900 mb-2">
                      {variant.name}:{" "}
                      <span className="font-normal text-foreground-600">
                        {selectedVariants[variant.name] || variant.options[0]}
                      </span>
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                      {variant.options.map((option) => {
                        const isSelected = (selectedVariants[variant.name] || variant.options[0]) === option;
                        return (
                          <button
                            key={option}
                            onClick={() =>
                              setSelectedVariants((prev) => ({
                                ...prev,
                                [variant.name]: option,
                              }))
                            }
                            className={`px-3 py-1.5 text-xs rounded-lg border transition-all cursor-pointer whitespace-nowrap ${
                              isSelected
                                ? "border-primary-500 bg-primary-50 text-primary-700 font-medium"
                                : "border-background-200 text-foreground-600 hover:border-secondary-300"
                            }`}
                          >
                            {option}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Quantity + Add to Cart */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center border border-background-200 rounded-lg">
                <button
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  className="w-9 h-9 flex items-center justify-center text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer rounded-l-lg"
                  disabled={!product.inStock}
                >
                  <i className="ri-subtract-line text-sm"></i>
                </button>
                <span className="w-10 h-9 flex items-center justify-center text-sm font-medium text-foreground-900 border-x border-background-200">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity((q) => Math.min(product.stockCount, q + 1))}
                  className="w-9 h-9 flex items-center justify-center text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer rounded-r-lg"
                  disabled={!product.inStock}
                >
                  <i className="ri-add-line text-sm"></i>
                </button>
              </div>
            </div>

            {/* Add to Cart button */}
            <button
              onClick={handleAddToCart}
              disabled={!product.inStock}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap text-sm mb-3"
            >
              <i className="ri-shopping-bag-line"></i>
              {product.inStock ? t("featured.add_to_cart") : t("product.out_of_stock")}
            </button>

            {/* View full details link */}
            <Link
              to={`/product/${product.slug}`}
              onClick={onClose}
              className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-900 transition-colors cursor-pointer"
            >
              {t("products.view_full_details")}
              <i className={`text-xs ${isAr ? "ri-arrow-left-line" : "ri-arrow-right-line"}`}></i>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}